/**
 * Combos: what a landed blow lets you do next.
 *
 * A strike that connects opens a short **cancel window**. Inside it, one of
 * that move's declared follow-ups can be thrown early, cutting the rest of the
 * recovery short — which is the entire mechanic. A move you can only throw
 * after its recovery has finished is not a combo, it is two punches.
 *
 * The routes are data, in `moves.json`, as a per-move `follows` list. So is the
 * window: each move names its own `cancel` frames, because a heavy blow that
 * staggers someone should buy more time than a jab that brushes them.
 *
 * Pure. It holds no strikes and no fighters — it is told when a blow landed and
 * when one missed, and it answers two questions: may this follow-up be thrown
 * right now, and what is the next hit in this chain worth.
 *
 * **Later hits are worth less.** Only consciousness is scaled down, never
 * wellness, and that split is the point: a combo still wears a body down at
 * full rate, it just stops being a way to switch the lights off in one string.
 * Without it the chain that opens the cancel window is also the chain that ends
 * the fight, and nothing else in the game matters.
 */

export class ComboConfigError extends Error {}

/** Used when a move does not name its own cancel window. */
const DEFAULT_CANCEL_FRAMES = 14;

/** Throws on routes that cannot be satisfied, so a typo fails at boot. */
export function validateRoutes(config) {
  const { moves, combo } = config ?? {};
  if (!moves) throw new ComboConfigError('move set has no "moves" object');

  for (const [name, move] of Object.entries(moves)) {
    if (move.follows !== undefined && !Array.isArray(move.follows)) {
      throw new ComboConfigError(`move "${name}" has a "follows" that is not a list`);
    }
    for (const next of move.follows ?? []) {
      if (!moves[next]) {
        throw new ComboConfigError(`move "${name}" follows into unknown move "${next}"`);
      }
      if (next === name) {
        // A move that cancels into itself is an infinite, and an infinite is
        // not a combo system, it is a hole in one.
        throw new ComboConfigError(`move "${name}" follows into itself`);
      }
    }
    if (move.cancel !== undefined && !(Number.isInteger(move.cancel) && move.cancel > 0)) {
      throw new ComboConfigError(`move "${name}" has a cancel window of ${move.cancel} frames`);
    }
    // A move cannot both refuse to be comboed and declare routes out of itself.
    if (move.comboable === false && (move.follows?.length || move.cancel !== undefined)) {
      throw new ComboConfigError(`move "${name}" is not comboable but declares a route out of itself`);
    }
  }

  const scaling = combo?.consciousnessScaling;
  if (!Array.isArray(scaling) || scaling.length === 0) {
    throw new ComboConfigError('combo.consciousnessScaling must be a non-empty list');
  }
  if (scaling[0] !== 1) {
    // The first hit of a chain is just a hit. If it were scaled, every number
    // anywhere else in the game would be quietly wrong.
    throw new ComboConfigError('combo.consciousnessScaling must start at 1');
  }
  for (let i = 1; i < scaling.length; i++) {
    if (!(scaling[i] > 0 && scaling[i] <= scaling[i - 1])) {
      throw new ComboConfigError(`combo.consciousnessScaling must fall; index ${i} does not`);
    }
  }
  if (!(combo.bufferFrames > 0)) {
    throw new ComboConfigError(`combo.bufferFrames is ${combo?.bufferFrames}`);
  }
  return config;
}

export function createCombo(config, { knows = () => true } = {}) {
  validateRoutes(config);

  const { frameRate, moves, combo } = config;
  const scaling = combo.consciousnessScaling;
  const bufferSeconds = combo.bufferFrames / frameRate;

  /** Moves landed in the chain so far, oldest first. */
  let chain = [];
  /** Seconds left on the cancel window opened by the last landed blow. */
  let windowLeft = 0;
  /**
   * A follow-up has been thrown and is still in the air.
   *
   * Without this the chain would die while the follow-up was still in startup:
   * a 16-frame cancel window and a 26-frame cross means the window shuts long
   * before the punch it authorised arrives.
   */
  let pending = false;

  let buffered = null;
  let bufferLeft = 0;

  function last() {
    return chain.length ? chain[chain.length - 1] : null;
  }

  function cancelSeconds(name) {
    return (moves[name]?.cancel ?? DEFAULT_CANCEL_FRAMES) / frameRate;
  }

  function clearBuffer() {
    buffered = null;
    bufferLeft = 0;
  }

  function reset() {
    chain = [];
    windowLeft = 0;
    pending = false;
    clearBuffer();
  }

  function allows(next) {
    if (!next || !moves[next] || !knows(next)) return false;
    const from = last();
    if (!from || windowLeft <= 0) return false;
    // `?.` rather than `.`: a chain can only hold names this table knows, and
    // `landed` is what enforces that — but this is the line that *crashed* when
    // it did not, and a route lookup is not worth a thrown TypeError.
    return (moves[from]?.follows ?? []).includes(next);
  }

  return {
    config: combo,

    /** The moves landed in this chain, oldest first. */
    get chain() {
      return [...chain];
    },
    get length() {
      return chain.length;
    },
    /** True while a follow-up may be cancelled into. */
    get open() {
      return windowLeft > 0;
    },
    get windowLeft() {
      return Math.max(0, windowLeft);
    },
    get pending() {
      return pending;
    },
    get buffered() {
      return buffered;
    },

    /**
     * What the *next* blow to land in this chain is worth in consciousness.
     * The first hit is always a full one; the list runs out into its own last
     * value, so a long chain does not fall off the end into nothing.
     */
    get scale() {
      return scaling[Math.min(chain.length, scaling.length - 1)];
    },

    /**
     * Legal follow-ups of a move, whether or not one is available now — and
     * only the ones this fighter has been taught. The routes in `moves.json`
     * are global; a style loadout is what turns them into a move list.
     */
    follows: (from) => (moves[from]?.follows ?? []).filter(knows),
    allows,

    /** A blow connected. Pushes the chain on and opens its cancel window. */
    landed(name) {
      pending = false;
      // A move that declares itself uncomboable ends the chain rather than
      // extending it, and opens no window of its own. Nothing but a weapon
      // swing does, and a weapon swing is a single committed act: an
      // unblockable opener with a cancel window is not a weapon, it is a win
      // button. An empty `follows` would have stopped anything being *offered*,
      // but the default cancel window would still have been handed out.
      // A move this table has never heard of is not a link, it is the end of
      // one. Anything else puts a name in the chain that no follow-up can be
      // looked up against — which is exactly how a grapple's display name got
      // in there and took the next `take()` with it.
      if (!moves[name] || moves[name].comboable === false) {
        reset();
        return 0;
      }
      chain.push(name);
      windowLeft = cancelSeconds(name);
      return windowLeft;
    },

    /**
     * A blow missed. A whiff ends a chain outright — the cancel window is
     * bought by *connecting*, and there is nothing to cancel out of.
     */
    missed() {
      reset();
    },

    /** Remembers a follow-up the fighter is not yet allowed to throw. */
    want(next) {
      if (!moves[next]) return false;
      buffered = next;
      bufferLeft = bufferSeconds;
      return true;
    },

    /**
     * The buffered move, if it may be thrown this instant. Does not clear it:
     * the caller commits only once the throw has actually been accepted, so a
     * refusal somewhere downstream does not silently eat the input.
     */
    take() {
      return allows(buffered) ? buffered : null;
    },

    /** The taken move was thrown. Clears the buffer and holds the chain open. */
    commit() {
      clearBuffer();
      pending = true;
    },

    update(dt) {
      if (!(dt > 0)) return;
      if (bufferLeft > 0) {
        bufferLeft -= dt;
        if (bufferLeft <= 0) clearBuffer();
      }
      // A chain with a punch still in the air does not time out. It ends when
      // that punch resolves, one way or the other.
      if (pending) return;
      if (windowLeft > 0) {
        windowLeft -= dt;
        if (windowLeft <= 0) reset();
      }
    },

    reset,
  };
}
